import { Award, Check, Dices, ScrollText } from 'lucide-preact';
import type { Achievement, Quest } from '@vm/shared';
import { claimAchievement, claimQuest, loadAchievements, loadQuests, rerollQuest } from '../state/actions';
import { replace, route } from '../state/nav';
import { achievements, claimableAchievements, quests } from '../state/store';
import { fmtNum } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { AsyncButton, Bar, Countdown, Empty, ErrorState, OnlineOnly, RewardChips, Screen, Spinner, Tabs } from '../ui/kit';

type Tab = 'daily' | 'weekly' | 'achievements';

function QuestCard(props: { q: Quest; rerolls: number }) {
  const q = props.q;
  const done = q.completed;
  return (
    <div class={`quest ${done ? 'is-done' : ''} ${q.claimed ? 'is-claimed' : ''}`}>
      <div class="quest-main">
        <div class="quest-title">
          <b>{q.title}</b>
          {!q.claimed && <Countdown to={q.expiresAt} class="quest-timer" />}
        </div>
        <p>{q.description}</p>
        <Bar value={Math.min(q.progress, q.goal)} max={q.goal} tone={done ? 'green' : 'blue'} label={`${fmtNum(Math.min(q.progress, q.goal))} / ${fmtNum(q.goal)}`} class="quest-bar" />
      </div>
      <div class="quest-side">
        <RewardChips reward={q.reward} />
        {q.claimed ? (
          <span class="arm-chip equipped">
            <Check size={14} strokeWidth={3.5} /> Claimed
          </span>
        ) : done ? (
          <AsyncButton class="primary" onClick={() => claimQuest(q.id)}>
            Claim
          </AsyncButton>
        ) : (
          <AsyncButton class="sm" disabled={props.rerolls <= 0} title="Swap for a different quest" onClick={() => rerollQuest(q.id)}>
            <Dices size={16} /> Reroll
          </AsyncButton>
        )}
      </div>
    </div>
  );
}

function QuestList(props: { kind: 'daily' | 'weekly' }) {
  const q = quests.value!;
  const list = props.kind === 'daily' ? q.daily : q.weekly;
  if (!list.length) return <Empty icon={<ScrollText size={44} />} title="No quests right now" body="New quests arrive soon." />;
  const sorted = [...list].sort((a, b) => Number(a.claimed) - Number(b.claimed) || Number(b.completed) - Number(a.completed));
  return (
    <>
      <div class="quest-meta">
        <span>
          <Dices size={16} /> {q.rerollsLeft} reroll{q.rerollsLeft === 1 ? '' : 's'} left today
        </span>
      </div>
      <div class="quest-list">
        {sorted.map((x) => (
          <QuestCard key={x.id} q={x} rerolls={q.rerollsLeft} />
        ))}
      </div>
    </>
  );
}

function AchievementCard(props: { a: Achievement }) {
  const a = props.a;
  return (
    <div class={`ach ${a.unlocked ? 'is-unlocked' : ''} ${a.claimed ? 'is-claimed' : ''}`}>
      <span class="ach-icon">
        <Icon name={a.icon} size={30} />
      </span>
      <div class="ach-main">
        <b>{a.title}</b>
        <p>{a.description}</p>
        {!a.unlocked && <Bar value={Math.min(a.progress, a.goal)} max={a.goal} tone="gem" label={`${fmtNum(Math.min(a.progress, a.goal))} / ${fmtNum(a.goal)}`} />}
        <RewardChips reward={a.reward} />
      </div>
      <div class="ach-side">
        {a.claimed ? (
          <span class="arm-chip equipped">
            <Check size={14} strokeWidth={3.5} /> Done
          </span>
        ) : a.unlocked ? (
          <AsyncButton class="primary sm" onClick={() => claimAchievement(a.id)}>
            Claim
          </AsyncButton>
        ) : null}
      </div>
    </div>
  );
}

function Achievements() {
  const load = useAsync(() => loadAchievements(), []);
  const list = achievements.value;
  if (!list) return load.error ? <ErrorState message={load.error} onRetry={load.reload} /> : <Spinner label="Loading achievements…" />;
  if (!list.length) return <Empty icon={<Award size={44} />} title="No achievements yet" />;
  const sorted = [...list].sort(
    (a, b) => Number(b.unlocked && !b.claimed) - Number(a.unlocked && !a.claimed) || Number(a.claimed) - Number(b.claimed) || b.progress / b.goal - a.progress / a.goal,
  );
  const done = list.filter((a) => a.unlocked).length;
  return (
    <>
      <div class="quest-meta">
        <span>
          <Award size={16} /> {done} / {list.length} unlocked
        </span>
      </div>
      <div class="ach-grid">
        {sorted.map((a) => (
          <AchievementCard key={a.id} a={a} />
        ))}
      </div>
    </>
  );
}

export function QuestsScreen() {
  const tab = (route.value.params.tab as Tab) || 'daily';
  const load = useAsync(() => loadQuests(), []);
  const q = quests.value;
  const claimable = (list: Quest[] | undefined): number => (list ?? []).filter((x) => x.completed && !x.claimed).length;
  return (
    <Screen
      title="Quests"
      icon={<ScrollText size={26} />}
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => replace('quests', { tab: t })}
          tabs={[
            { id: 'daily', label: 'Daily', badge: claimable(q?.daily) },
            { id: 'weekly', label: 'Weekly', badge: claimable(q?.weekly) },
            { id: 'achievements', label: 'Achievements', badge: claimableAchievements.value },
          ]}
        />
      }
    >
      <OnlineOnly feature="Quests">
        <div class="scr-scroll narrow" key={tab}>
          {tab === 'achievements' ? (
            <Achievements />
          ) : !q ? (
            load.error ? (
              <ErrorState message={load.error} onRetry={load.reload} />
            ) : (
              <Spinner label="Loading quests…" />
            )
          ) : (
            <QuestList kind={tab} />
          )}
        </div>
      </OnlineOnly>
    </Screen>
  );
}
